import { checkPermissions, colors } from "@elit-hair/elit-tools";
import { Avatar, Divider, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbLogout } from "react-icons/tb";
import { Link } from "react-router-dom";

import LanguageSelector from "@/components/LanguageSelector";
import routes from "@/constants/routes";
import useAuth from "@/providers/hooks/useAuth";

export type UserDropdownProps = {};

function UserDropdown() {
    const { t } = useTranslation();
    const { userData, handleLogout } = useAuth();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogoutClick = () => {
        handleLogout();
        handleClose();
    };

    const userInitials = userData?.name
        ? userData.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
        : "EK";

    const allowedRoutes = useMemo(
        () =>
            routes
                .filter((route) => {
                    if (!route.icon || route.hidden) return false;
                    if (!checkPermissions(userData?.permissionCodes, route.permissions)) return false;
                    if (route.path === "/notifications") return false;
                    return true;
                })
                .slice(0, 3),
        [userData]
    );

    return (
        <>
            <IconButton
                onClick={handleClick}
                className="!shrink-0 !grow-0 -translate-y-[2px] md:!ml-2"
                color="inherit"
                aria-controls={open ? "user-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "transparent",
                        border: "2px solid rgba(255,255,255,0.5)",
                        fontSize: "0.875rem",
                        color: "rgba(255,255,255,0.5)",
                        "&:hover": {
                            borderColor: "rgba(255,255,255,0.75)",
                            color: "rgba(255,255,255,0.75)",
                        },
                    }}
                >
                    {userInitials}
                </Avatar>
            </IconButton>

            <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "user-button",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            width: "min(400px, calc(100vw - 2rem))",
                            minWidth: 300,
                        },
                    },
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                {/* User Info Section - Updated Header */}
                <div className="flex items-center gap-2 px-4 py-3">
                    <Avatar
                        sx={{
                            width: 48,
                            height: 48,
                            bgcolor: colors.elitBlue,
                            fontSize: "1.25rem",
                            fontWeight: "600",
                        }}
                    >
                        {userInitials}
                    </Avatar>
                    <div className=" translate-y-[2px]">
                        <Typography variant="h6" className="!font-semibold !leading-none  !truncate">
                            {userData?.name || "Elit Klinik"}
                        </Typography>
                        <Typography variant="caption" className="!text-gray-500">
                            ID: {userData?.id}
                        </Typography>
                    </div>
                </div>

                {/* Language Selector */}
                <div className="px-4 py-2">
                    <LanguageSelector />
                </div>
                <Divider />

                {/* Navigation Items - first 3 permitted routes */}
                {allowedRoutes.map((route) => (
                    <MenuItem key={route.path} component={Link} to={route.path} onClick={handleClose} className="!py-3">
                        {route.icon && (
                            <span className="!mr-3 !text-gray-600">
                                <route.icon />
                            </span>
                        )}
                        <Typography variant="body2">{t(route.title || route.mobileTitle || "")}</Typography>
                    </MenuItem>
                ))}

                <Divider />

                {/* Logout */}
                <MenuItem onClick={handleLogoutClick} className="!py-3 !text-red-600">
                    <TbLogout className="!mr-3 !text-red-600" size={16} />
                    <Typography variant="body2" className="!text-red-600">
                        {t("logout")}
                    </Typography>
                </MenuItem>
            </Menu>
        </>
    );
}

export default UserDropdown;
